// Ellora canonical date helpers — always IST, always DD-MMM-YY (e.g. "05-Sep-26").
// Deterministic: never relies on browser/OS locale for separator characters.
const _M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const _ist = (d) => { const t = new Date(d); return isNaN(t.getTime()) ? null : new Date(t.getTime() + 19800000); };

// Indian currency formatter (₹ 1,00,00,000)
export const inr = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹ 0';
  const x = Math.round(Number(n));
  const s = x.toString();
  let lastThree = s.substring(s.length - 3);
  const otherNumbers = s.substring(0, s.length - 3);
  if (otherNumbers !== '') lastThree = ',' + lastThree;
  return '₹ ' + otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
};

// Tight INR formatter used by HR, Contracts, Closeout — no space after ₹,
// integer rounding, sign aware, returns "—" for null/undefined (callers
// already coerce 0 → "₹0" on purpose for KPI tiles).
export const formatINR = (v, { dashIfNull = false } = {}) => {
  if (v === null || v === undefined) return dashIfNull ? '—' : '₹0';
  const n = Number(v);
  if (!Number.isFinite(n)) return dashIfNull ? '—' : '₹0';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

// Item 7 (Fixes_Combined 30-Sep-2026): money on Procurement screens (CS, PO
// detail, quote rows) must show ₹ with exactly 2 decimals — never whole
// rupees and never compact "K"/"L"/"Cr" notation.
export const inr2 = (n) => {
  const v = Number(n);
  if (n === null || n === undefined || !Number.isFinite(v)) return '₹\u00A00.00';
  const sign = v < 0 ? '-' : '';
  // Followup round 2, P8: a regular space let narrow PO-detail cells wrap
  // right after the ₹ sign — a non-breaking space keeps it with the amount.
  return `${sign}₹\u00A0${Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const inrCompact = (n) => {
  if (!n) return '₹ 0';
  const num = Number(n);
  if (num >= 10000000) return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹ ${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `₹ ${(num / 1000).toFixed(1)} K`;
  return `₹ ${num}`;
};

// "2026-10-03" — today's calendar date in IST, YYYY-MM-DD. Use for date-input
// defaults/max (e.g. Missed Punch) so a user near the IST midnight boundary
// never sees yesterday's date because the device/server clock reads UTC.
export const todayISTStr = () => {
  const ist = _ist(new Date());
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, '0')}-${String(ist.getUTCDate()).padStart(2, '0')}`;
};

// "05-Sep-26" — DD-MMM-YY, always IST. The ONLY canonical date formatter.
// Deterministic: never relies on browser/OS locale for separator characters.
// Every component MUST import and use this — no local fmtDate/fmt variants.
export const formatDate = (d) => {
  if (!d) return '—';
  const ist = _ist(d);
  if (!ist) return String(d);
  const dd = String(ist.getUTCDate()).padStart(2, '0');
  const yr = String(ist.getUTCFullYear()).slice(-2);
  return `${dd}-${_M[ist.getUTCMonth()]}-${yr}`;
};

// "08:30 PM" — time-only, always IST.
// Handles both full UTC ISO timestamps (old format) and plain "HH:MM" 24-hour
// strings stored in hr_attendance since 30-Sep-2026 (already in IST).
export const formatISTTime = (d) => {
  if (!d) return '—';
  const s = String(d).trim();
  // "H:MM" or "HH:MM" — stored as IST 24-hour time, convert to 12-hour display
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [hh, mm] = s.split(':').map(Number);
    if (!isNaN(hh) && !isNaN(mm) && hh >= 0 && hh < 24 && mm >= 0 && mm < 60) {
      const h12 = String(hh % 12 || 12).padStart(2, '0');
      return `${h12}:${String(mm).padStart(2, '0')} ${hh >= 12 ? 'PM' : 'AM'}`;
    }
    return s; // malformed — return as-is
  }
  const ist = _ist(d);
  if (!ist) return '';
  const h = ist.getUTCHours();
  const m = String(ist.getUTCMinutes()).padStart(2, '0');
  const h12 = String(h % 12 || 12).padStart(2, '0');
  return `${h12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
};

// "05-Sep-26 08:30 PM" — date + time, always IST.
export const formatDateTime = (d) => {
  if (!d) return '—';
  const ist = _ist(d);
  if (!ist) return '';
  const dd = String(ist.getUTCDate()).padStart(2, '0');
  const yr = String(ist.getUTCFullYear()).slice(-2);
  const h = ist.getUTCHours();
  const m = String(ist.getUTCMinutes()).padStart(2, '0');
  const h12 = String(h % 12 || 12).padStart(2, '0');
  return `${dd}-${_M[ist.getUTCMonth()]}-${yr} ${h12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
};

// "Sep-26" — month label, IST. For payroll periods, MIS headers, etc.
export const formatMonth = (d) => {
  if (!d) return '—';
  const ist = _ist(d);
  if (!ist) return '';
  return `${_M[ist.getUTCMonth()]}-${String(ist.getUTCFullYear()).slice(-2)}`;
};

const _WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const _MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

// "Sun" — weekday abbreviation only, IST.
export const formatWeekday = (d) => {
  const ist = _ist(d);
  return ist ? _WD[ist.getUTCDay()] : '';
};

// "05" — day-of-month only, IST, zero-padded.
export const formatDayOfMonth = (d) => {
  const ist = _ist(d);
  return ist ? String(ist.getUTCDate()).padStart(2, '0') : '';
};

// "05-Oct" — day + month only, IST, no year. For birthdays etc.
export const formatDayMonth = (d) => {
  if (!d) return '—';
  const ist = _ist(d);
  if (!ist) return String(d);
  return `${String(ist.getUTCDate()).padStart(2, '0')}-${_M[ist.getUTCMonth()]}`;
};

// "September" — full month name, IST. For calendar grouping headers.
export const formatMonthName = (d) => {
  const ist = _ist(d);
  return ist ? _MONTH_LONG[ist.getUTCMonth()] : '';
};

// Deprecated alias, kept for the couple of existing call sites — same
// canonical output as formatDateTime. Prefer formatDateTime in new code.
export const formatISTDateTime = formatDateTime;

export const statusColor = (s) => {
  const m = {
    Active: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Completed: 'bg-slate-100 text-slate-700 border border-slate-200',
    Delayed: 'bg-red-50 text-red-700 border border-red-200',
    Pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    'In Progress': 'bg-blue-50 text-blue-700 border border-blue-200',
    Approved: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Submitted: 'bg-blue-50 text-blue-700 border border-blue-200',
    'Pending Approval': 'bg-amber-50 text-amber-700 border border-amber-200',
    Open: 'bg-red-50 text-red-700 border border-red-200',
    Closed: 'bg-slate-100 text-slate-600 border border-slate-200',
    Working: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Maintenance: 'bg-amber-50 text-amber-700 border border-amber-200',
    Delivered: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Hot: 'bg-red-50 text-red-700 border border-red-200',
    Warm: 'bg-amber-50 text-amber-700 border border-amber-200',
    Cold: 'bg-slate-100 text-slate-600 border border-slate-200',
    Low: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Medium: 'bg-amber-50 text-amber-700 border border-amber-200',
    High: 'bg-red-50 text-red-700 border border-red-200',
  };
  return m[s] || 'bg-slate-100 text-slate-700 border border-slate-200';
};

export const fmtDMY = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()];
  return `${String(dt.getDate()).padStart(2, '0')}-${m}-${dt.getFullYear()}`;
};
